import React, { useEffect, useId, useState } from 'react';
import Dialog from '../common/Dialog';

const MIN_REASON = 10;

/** ISO instant → value for <input type="datetime-local"> in the browser's local time. */
export const toLocalInput = (iso: string | null | undefined): string => {
  const ms = Date.parse(iso || '');
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const fromLocalInput = (value: string): string | null => {
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
};

export interface VisitTimeDialogProps {
  open: boolean;
  /** 'close' = end an abandoned visit (VIS-07); 'correct' = fix a completed visit's times (VIS-08) */
  mode: 'close' | 'correct';
  summary: string;
  start: string | null;
  end?: string | null;
  busy?: boolean;
  error?: string | null;
  onConfirm: (times: { startTime?: string; endTime: string }, reason: string) => void | Promise<void>;
  onCancel: () => void;
}

/** Corrected time(s) + a required reason (at least 10 characters). Audited on the server. */
const VisitTimeDialog: React.FC<VisitTimeDialogProps> = ({ open, mode, summary, start, end, busy, error, onConfirm, onCancel }) => {
  const id = useId();
  const [startValue, setStartValue] = useState('');
  const [endValue, setEndValue] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (!open) return;
    setStartValue(toLocalInput(start));
    setEndValue(toLocalInput(end || null));
    setReason('');
  }, [open, start, end]);
  const startIso = fromLocalInput(startValue);
  const endIso = fromLocalInput(endValue);
  const order = startIso && endIso ? Date.parse(endIso) > Date.parse(startIso) : Boolean(endIso);
  const valid = Boolean(endIso) && order && reason.trim().length >= MIN_REASON;
  return (
    <Dialog open={open} onClose={onCancel} labelledBy={`${id}-t`}>
      <h2 id={`${id}-t`} className="text-lg font-semibold text-gray-900">{mode === 'close' ? 'Close visit' : 'Correct visit times'}</h2>
      <p className="mt-1 text-sm text-gray-600">{summary}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-gray-700">
          Start
          <input type="datetime-local" className="mt-1 block min-h-[44px] w-full rounded-md border border-gray-300 px-2 text-base md:text-sm disabled:bg-gray-100" value={startValue} disabled={mode === 'close'} onChange={(e) => setStartValue(e.target.value)} />
        </label>
        <label className="text-sm text-gray-700">
          {mode === 'close' ? 'Actual end' : 'End'}
          <input type="datetime-local" className="mt-1 block min-h-[44px] w-full rounded-md border border-gray-300 px-2 text-base md:text-sm" value={endValue} onChange={(e) => setEndValue(e.target.value)} />
        </label>
      </div>
      {!order && endValue && <p className="mt-1 text-xs text-red-700">The end must be after the start.</p>}
      <label htmlFor={`${id}-r`} className="mt-4 block text-sm font-medium text-gray-700">Reason <span className="text-red-600">*</span></label>
      <textarea id={`${id}-r`} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-base md:text-sm" placeholder="e.g. Clinician forgot to end the visit; confirmed end time with the family" />
      <p className="text-xs text-gray-500">At least {MIN_REASON} characters. Recorded in the audit log.</p>
      {error && <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm">Cancel</button>
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => endIso && onConfirm({ ...(mode === 'correct' && startIso ? { startTime: startIso } : {}), endTime: endIso }, reason.trim())}
          className="min-h-[44px] rounded-md bg-slate-800 px-4 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? 'Saving…' : mode === 'close' ? 'Close visit' : 'Save correction'}
        </button>
      </div>
    </Dialog>
  );
};

export default VisitTimeDialog;
